<template>
    <div class="mobile-signing-progress">
        <!-- Progress Bar -->
        <div class="progress-bar-container">
            <div
                class="progress-bar-fill"
                :style="{ width: completionPercentage + '%' }"
            ></div>
        </div>

        <!-- Progress Stats -->
        <div class="progress-stats">
            <div class="stat-item">
                <span class="stat-value">{{ completedCount }}</span>
                <span class="stat-label">Completed</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
                <span class="stat-value">{{ pendingCount }}</span>
                <span class="stat-label">Remaining</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item highlight">
                <span class="stat-value">{{ completionPercentage }}%</span>
                <span class="stat-label">Complete</span>
            </div>
        </div>

        <!-- Section Breakdown (if multiple signers) -->
        <div class="section-breakdown" v-if="showBreakdown">
            <div
                class="section-item"
                :class="{ 'current': section.isCurrent }"
                v-for="section in sections"
                :key="section.signeeType"
            >
                <div class="section-header">
                    <span
                        class="section-badge"
                        :class="`badge-${section.signeeType}`"
                    >
                        {{ section.label }}
                    </span>
                    <span class="section-count">
                        {{ section.completed }}/{{ section.total }}
                    </span>
                </div>
                <div class="section-progress">
                    <div
                        class="section-progress-fill"
                        :style="{ width: section.percentage + '%' }"
                    ></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MobileSigningProgress',
    props: {
        documentFields: {
            type: Array,
            default: () => []
        },
        completedFields: {
            type: Array,
            default: () => []
        },
        userRole: {
            type: String,
            default: 'student'
        },
        showBreakdown: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        totalCount() {
            return this.documentFields.length;
        },

        completedCount() {
            return this.completedFields.length;
        },

        pendingCount() {
            return this.totalCount - this.completedCount;
        },

        completionPercentage() {
            if (this.totalCount === 0) return 100;
            return Math.round((this.completedCount / this.totalCount) * 100);
        },

        sections() {
            // Group fields by signee type
            const groupedFields = {};
            const groupedCompleted = {};

            this.documentFields.forEach(field => {
                const annotation = field.annotations?.find(a => a.signee_type);
                const signeeType = annotation?.signee_type || field.assigned_to?.role || 'unassigned';

                if (!groupedFields[signeeType]) {
                    groupedFields[signeeType] = [];
                }
                groupedFields[signeeType].push(field);
            });

            this.completedFields.forEach(field => {
                const annotation = field.annotations?.find(a => a.signee_type);
                const signeeType = annotation?.signee_type || field.assigned_to?.role || 'unassigned';

                if (!groupedCompleted[signeeType]) {
                    groupedCompleted[signeeType] = 0;
                }
                groupedCompleted[signeeType]++;
            });

            // Build sections array
            const sections = [];
            const signeeOrder = ['student', 'guardian', 'instructor', 'staff', 'admin', 'unassigned'];

            signeeOrder.forEach(signeeType => {
                if (groupedFields[signeeType]) {
                    const total = groupedFields[signeeType].length;
                    const completed = groupedCompleted[signeeType] || 0;
                    sections.push({
                        signeeType,
                        label: this.formatSigneeType(signeeType),
                        total,
                        completed,
                        percentage: total > 0 ? Math.round((completed / total) * 100) : 100,
                        isCurrent: signeeType === this.userRole
                    });
                }
            });

            return sections;
        }
    },
    methods: {
        formatSigneeType(type) {
            if (!type || type === 'unassigned') return 'Other';
            const labels = {
                student: 'Student',
                guardian: 'Guardian',
                instructor: 'Instructor',
                staff: 'Staff',
                admin: 'Admin'
            };
            return labels[type] || type;
        }
    }
};
</script>

<style scoped>
.mobile-signing-progress {
    background-color: var(--ds-signing-card, #FFFFFF);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Progress Bar */
.progress-bar-container {
    width: 100%;
    height: 8px;
    background-color: var(--ds-signing-border, #E2E8F0);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 16px;
}

.progress-bar-fill {
    height: 100%;
    background-color: var(--ds-signing-success, #059669);
    border-radius: 4px;
    transition: width 0.4s ease;
}

/* Progress Stats */
.progress-stats {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
}

.stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--ds-signing-text, #1E293B);
    line-height: 1.2;
}

.stat-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--ds-signing-muted, #64748B);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
}

.stat-item.highlight .stat-value {
    color: var(--ds-signing-success, #059669);
}

.stat-divider {
    width: 1px;
    height: 32px;
    background-color: var(--ds-signing-border, #E2E8F0);
}

/* Section Breakdown */
.section-breakdown {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--ds-signing-border, #E2E8F0);
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.section-item {
    padding: 12px;
    background-color: var(--ds-signing-bg, #F8FAFC);
    border-radius: 8px;
    transition: all 0.2s;
}

.section-item.current {
    background-color: rgba(37, 99, 235, 0.08);
    border: 1px solid rgba(37, 99, 235, 0.2);
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.section-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-student {
    background-color: #3B82F6;
}

.badge-guardian {
    background-color: #10B981;
}

.badge-instructor {
    background-color: #F59E0B;
}

.badge-staff {
    background-color: #8B5CF6;
}

.badge-admin {
    background-color: #EF4444;
}

.badge-unassigned {
    background-color: #6B7280;
}

.section-count {
    font-size: 13px;
    font-weight: 600;
    color: var(--ds-signing-text, #1E293B);
}

.section-progress {
    width: 100%;
    height: 4px;
    background-color: var(--ds-signing-border, #E2E8F0);
    border-radius: 2px;
    overflow: hidden;
}

.section-progress-fill {
    height: 100%;
    background-color: var(--ds-signing-success, #059669);
    border-radius: 2px;
    transition: width 0.4s ease;
}

.section-item.current .section-progress-fill {
    background-color: var(--ds-signing-primary, #2563EB);
}
</style>
